import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout, Menu } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getSidebarMenuItems,
  getMenuSelection,
  routeByMenuKey,
} from "./sidebarMenuItems";
import { CustomButton } from "src/components/ui";
import { cn } from "src/util/cn";
import { SidebarChevronIcon, SidebarCollapseIcon } from "src/assets/svgs";
import "./sidebar-menu.css";
import { requestGuardedNavigation } from "src/util/navigationGuard";

const { Sider } = Layout;
const ChevronIcon = SidebarChevronIcon as unknown as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const CollapseIcon = SidebarCollapseIcon as unknown as React.ComponentType<
  React.SVGProps<SVGSVGElement>
>;
const ROOT_SUBMENU_KEYS = [
  "clients",
  "tachograph",
  "manage_units",
  "user_management",
  "warehouse",
  "administrator",
];

export const Sidebar: React.FC = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const { selectedKeys, openKeys: routeOpenKeys } = useMemo(
    () => getMenuSelection(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setOpenKeys(routeOpenKeys);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [routeOpenKeys, collapsed]);

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  const handleOpenChange = (keys: string[]) => {
    const latestOpenKey = keys.find((key) => !openKeys.includes(key));

    if (latestOpenKey && ROOT_SUBMENU_KEYS.includes(latestOpenKey)) {
      setOpenKeys([latestOpenKey]);
      return;
    }

    setOpenKeys(keys);
  };

  const menuItems = getSidebarMenuItems(t);

  const handleMenuClick = useCallback(
    ({ key }: { key: string }) => {
      const path = routeByMenuKey[key];
      if (path) {
        requestGuardedNavigation(() => navigate(path));
      }
    },
    [navigate]
  );

  return (
    <Sider
      id="crm-sidebar-shell"
      trigger={null}
      collapsible
      collapsed={collapsed}
      className={cn(
        "height-full !bg-white relative sidebar-shell",
        collapsed ? "left-sidebar-widths-collpase" : "left-sidebar-widths"
      )}
    >
      <CustomButton
        className={cn(
          "sidebar-collapse-button cursor-pointer !p-0 !w-6 !h-6 !bg-white !absolute !right-2 !bottom-5 !z-50 flex items-center justify-center",
          "transition-all duration-300",
          "border !border-[rgba(178,184,207,1)]",
          "hover:!bg-[#ebf0fb] hover:!border-[rgba(56,103,214,1)]"
        )}
        onClick={toggleSidebar}
        type="border"
        icon={
          <CollapseIcon
            aria-hidden
            className={cn("sidebar-collapse-icon transition-all duration-300", {
              "rotate-180": !collapsed,
            })}
          />
        }
      />

      <Menu
        theme="dark"
        mode="inline"
        inlineCollapsed={collapsed}
        selectedKeys={selectedKeys}
        items={menuItems}
        getPopupContainer={(node) =>
          document.getElementById("crm-sidebar-shell") ?? node.parentElement ?? document.body
        }
        triggerSubMenuAction="hover"
        className={cn("custom-menu-implementation", {
          "custom-menu-collapsed": collapsed,
          "custom-menu-unCollapsed": !collapsed,
        })}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        onClick={handleMenuClick}
        expandIcon={({ isOpen }) => (
          <ChevronIcon
            aria-hidden
            className={cn("sidebar-menu-toggle", {
              "sidebar-menu-toggle-open": isOpen,
            })}
          />
        )}
      />
    </Sider>
  );
};

export default Sidebar;
